import UIButton, { UIButtonProps } from 'UIComponents/button/UIButton';
import styled, { css } from 'foundations-theming-specialty/utils';
import { CSSObject } from 'foundations-theming/utils';
import { fwcColor } from 'foundations-theming/utils/fwc';
import { BREEZE_SPECIAL } from '../../../../constants/BreezeUse';
import { AIBaseButtonProps } from '../AIBaseButton';

const DropdownButtonIconStyle = css`
  .uiDropdown__buttonContents {
    display: inline-block;
    .uiDropdown__buttonLabel {
      margin-right: ${({ theme }) => theme.space['200']};
    }
  }
  [class^='AbstractDropdown'] {
    display: inline;
    [class^='DropdownCaret'] {
      margin-left: ${({ theme }) => theme.space['200']};
    }
  }
`;

const IconStyle = css`
  .ai-icon {
    position: relative;
  }
`;

type StyledUIButtonProps = UIButtonProps & {
  cssstyles?: CSSObject;
};

export const BreezePrimaryAIBaseButton = styled(UIButton).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'cssstyles',
})<StyledUIButtonProps>`
  border-radius: ${({ theme }) => theme.borderRadius['full']} !important;
  color: ${({ theme }) => theme.color['fill-surface-default']} !important;
  border: none !important;
  background: ${({ theme }) =>
    theme.color[fwcColor('fwc-ai-button-fill-primary-default')]} !important;
  &:hover {
    background: ${({ theme }) =>
      theme.color[fwcColor('fwc-ai-button-fill-primary-hover')]} !important;
  }
  &:active {
    background: ${({ theme }) =>
      theme.color[fwcColor('fwc-ai-button-fill-primary-pressed')]} !important;
  }
  ${DropdownButtonIconStyle}
  ${IconStyle}

  &&& {
    ${({ cssstyles }) => cssstyles}
    &.disabled {
      color: ${({ theme }) =>
        theme.color['breeze-text-component-disabled']} !important;
      border: none !important;
      background: ${({ theme }) =>
        theme.color['breeze-fill-primary-disabled']} !important;
    }
  }
`;

export const BreezeSecondaryAIBaseButton = styled(UIButton).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'cssstyles',
})<StyledUIButtonProps>`
  border-radius: ${({ theme }) => theme.borderRadius['full']} !important;

  ${DropdownButtonIconStyle}
  ${IconStyle}

  &&& {
    border: 1px solid transparent !important;
    color: ${({ theme }) =>
      theme.color['breeze-text-secondary-default']} !important;
    background: ${({ theme }) =>
      `linear-gradient(${theme.color['breeze-fill-secondary-default']}, ${
        theme.color['breeze-fill-secondary-default']
      }) padding-box, linear-gradient(${theme.color['fill-surface-default']}, ${
        theme.color['fill-surface-default']
      }) padding-box, ${
        theme.color[fwcColor('fwc-ai-button-border-secondary-default')]
      } border-box`} !important;
    &:hover {
      border: 1px solid transparent !important;
      color: ${({ theme }) =>
        theme.color['breeze-text-secondary-hover']} !important;
      background: ${({ theme }) =>
        `linear-gradient(${theme.color['breeze-fill-secondary-hover']}, ${
          theme.color['breeze-fill-secondary-hover']
        }) padding-box, linear-gradient(${
          theme.color['fill-surface-default']
        }, ${theme.color['fill-surface-default']}) padding-box, ${
          theme.color[fwcColor('fwc-ai-button-border-secondary-hover')]
        } border-box`} !important;
    }
    &:active {
      border: 1px solid transparent !important;
      color: ${({ theme }) =>
        theme.color['breeze-text-secondary-pressed']} !important;
      background: ${({ theme }) =>
        `linear-gradient(${theme.color['breeze-fill-secondary-pressed']}, ${
          theme.color['breeze-fill-secondary-pressed']
        }) padding-box, linear-gradient(${
          theme.color['fill-surface-default']
        }, ${theme.color['fill-surface-default']}) padding-box, ${
          theme.color[fwcColor('fwc-ai-button-border-secondary-pressed')]
        } border-box`} !important;
    }
    ${({ cssstyles }) => cssstyles}
    &.disabled {
      color: ${({ theme }) =>
        theme.color['breeze-text-component-disabled']} !important;
      border-color: ${({ theme }) =>
        theme.color['border-core-subtle-pressed']} !important;
      background: ${({ theme }) =>
        theme.color['breeze-fill-secondary-disabled']} !important;
    }
  }
`;

export const BreezeTertiaryAIBaseButton = styled(UIButton).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'cssstyles',
})<StyledUIButtonProps>`
  border-radius: ${({ theme }) => theme.borderRadius['full']} !important;

  ${DropdownButtonIconStyle}
  ${IconStyle}

  &&& {
    color: ${({ theme }) =>
      theme.color['breeze-text-tertiary-default']} !important;
    border: 1px solid
      ${({ theme }) => theme.color['breeze-border-tertiary-default']} !important;
    background: ${({ theme }) =>
      theme.color['fill-surface-default']} !important;
    &:hover {
      color: ${({ theme }) =>
        theme.color['breeze-text-tertiary-default']} !important;
      background: ${({ theme }) =>
        theme.color['breeze-fill-tertiary-hover']} !important;
    }
    &:active {
      color: ${({ theme }) =>
        theme.color['breeze-text-tertiary-default']} !important;
      background: ${({ theme }) =>
        theme.color['breeze-fill-tertiary-pressed']} !important;
    }
    ${({ cssstyles }) => cssstyles}
    &.disabled {
      color: ${({ theme }) =>
        theme.color['breeze-text-component-disabled']} !important;
      border: 1px solid
        ${({ theme }) => theme.color['border-core-subtle-pressed']} !important;
      background: ${({ theme }) =>
        theme.color['breeze-fill-primary-disabled']} !important;
    }
  }
`;

export const BreezeSpecialAIBaseButton = styled(UIButton).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'cssstyles',
})<StyledUIButtonProps>`
  border-radius: ${({ theme }) => theme.borderRadius['full']} !important;
  &:not(.disabled) {
    background: ${({ theme }) =>
      theme.color['breeze-fill-special-btn-alt-default']} !important;
    color: ${({ theme }) => theme.color['fill-surface-default']} !important;
    border: none !important;
    &:hover {
      background: ${({ theme }) =>
        theme.color[fwcColor('fwc-ai-button-fill-primary-hover')]};
    }
    &:active {
      background: ${({ theme }) =>
        theme.color[fwcColor('fwc-ai-button-fill-primary-pressed')]};
    }
  }
  ${DropdownButtonIconStyle}
  ${IconStyle}

  &&& {
    ${({ cssstyles }) => cssstyles}
    &.disabled {
      color: ${({ theme }) =>
        theme.color['breeze-text-component-disabled']} !important;
      border-color: ${({ theme }) =>
        theme.color['border-core-subtle-pressed']} !important;
      background: ${({ theme }) =>
        theme.color['breeze-fill-primary-disabled']} !important;
    }
  }
`;

export const BreezeTransparentAIBaseButton = styled(UIButton).withConfig({
  shouldForwardProp: (prop: string | number) => prop !== 'cssstyles',
})<StyledUIButtonProps>`
  border-radius: ${({ theme }) => theme.borderRadius['full']} !important;

  ${DropdownButtonIconStyle}
  ${IconStyle}

  &&& {
    color: ${({ theme }) =>
      theme.color['breeze-text-transparent-default']} !important;
    border: none !important;
    background: transparent !important;
    &:not(.disabled):hover {
      color: ${({ theme }) =>
        theme.color['breeze-text-transparent-default']} !important;
      background: ${({ theme }) =>
        theme.color['breeze-fill-tertiary-hover']} !important;
    }
    &:not(.disabled):active {
      color: ${({ theme }) =>
        theme.color['breeze-text-transparent-default']} !important;
      background: ${({ theme }) =>
        theme.color['breeze-fill-tertiary-pressed']} !important;
    }
    ${({ cssstyles }) => cssstyles}
    &.disabled {
      color: ${({ theme }) =>
        theme.color['breeze-text-component-disabled']} !important;
      border-color: transparent !important;
    }
  }
`;

export const getStyledAIButton = (use: AIBaseButtonProps['use']) => {
  switch (use) {
    case BREEZE_SPECIAL:
      return BreezeSpecialAIBaseButton;
    case 'primary':
      return BreezePrimaryAIBaseButton;
    case 'secondary':
      return BreezeSecondaryAIBaseButton;
    case 'tertiary':
      return BreezeTertiaryAIBaseButton;
    case 'transparent':
      return BreezeTransparentAIBaseButton;
    default:
      return BreezePrimaryAIBaseButton;
  }
};
